import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Star, Check, X, Ban, Radio } from 'lucide-react';
import api from '@/lib/adminApi';
import { ADMIN } from '@/constants/testIds';
import { EmptyState } from '@/components/admin/EmptyState';

const STATUS_STYLE = {
  approved: 'bg-success/15 text-success',
  pending: 'bg-yellow-400/15 text-yellow-400',
  suspended: 'bg-destructive/15 text-destructive',
  rejected: 'bg-white/10 text-white/50',
};

export default function AdminProviders() {
  const [providers, setProviders] = useState(null);
  const [acting, setActing] = useState('');

  const fetchProviders = () => api.get('/admin/providers').then((r) => setProviders(r.data.providers));
  useEffect(() => { fetchProviders(); }, []);

  const runAction = async (id, action) => {
    setActing(id + action);
    try {
      await api.post(`/admin/providers/${id}/action`, { action });
      toast.success(`Provider ${action}d`);
      fetchProviders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setActing('');
    }
  };

  if (!providers) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-white sm:text-3xl">Signal Providers</h1>
        <p className="mt-1 text-sm text-white/50">Review, approve, feature and manage signal providers.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {providers.length === 0 ? (
          <div className="md:col-span-2 xl:col-span-3">
            <EmptyState icon={Radio} title="No providers yet" description="Signal providers will appear here once you connect your app backend (Dashboard → Integrations). You'll then be able to approve, feature and manage them." />
          </div>
        ) : providers.map((p) => (
          <div key={p.id} data-testid={ADMIN.providerRow} className="rounded-2xl border border-white/10 bg-surface/70 p-5">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-brand/15 font-display font-bold text-brand">{p.name.charAt(0)}</div>
                <div>
                  <p className="flex items-center gap-1.5 font-medium text-white">{p.name} {p.featured && <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />}</p>
                  <p className="text-xs text-white/40">{p.handle} · Rank #{p.rank}</p>
                </div>
              </div>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLE[p.status]}`}>{p.status}</span>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-elevated py-2"><p className="font-display text-lg font-bold text-white">{p.subscribers.toLocaleString()}</p><p className="text-[10px] uppercase tracking-wider text-white/40">Subs</p></div>
              <div className="rounded-lg bg-elevated py-2"><p className="font-display text-lg font-bold text-success">{p.win_rate}%</p><p className="text-[10px] uppercase tracking-wider text-white/40">Win</p></div>
              <div className="rounded-lg bg-elevated py-2"><p className="font-display text-lg font-bold text-brand">${(p.revenue / 1000).toFixed(1)}k</p><p className="text-[10px] uppercase tracking-wider text-white/40">Rev</p></div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {p.status === 'pending' && (
                <>
                  <button disabled={acting} onClick={() => runAction(p.id, 'approve')} data-testid={ADMIN.providerApprove} className="flex items-center gap-1.5 rounded-lg bg-success/15 px-3 py-1.5 text-xs font-medium text-success hover:bg-success/25"><Check className="h-3.5 w-3.5" /> Approve</button>
                  <button disabled={acting} onClick={() => runAction(p.id, 'reject')} data-testid={ADMIN.providerReject} className="flex items-center gap-1.5 rounded-lg bg-white/5 px-3 py-1.5 text-xs font-medium text-white/70 hover:bg-white/10"><X className="h-3.5 w-3.5" /> Reject</button>
                </>
              )}
              {p.status === 'approved' && (
                <>
                  <button disabled={acting} onClick={() => runAction(p.id, p.featured ? 'unfeature' : 'feature')} data-testid={ADMIN.providerFeature} className="flex items-center gap-1.5 rounded-lg bg-yellow-400/15 px-3 py-1.5 text-xs font-medium text-yellow-400 hover:bg-yellow-400/25"><Star className="h-3.5 w-3.5" /> {p.featured ? 'Unfeature' : 'Feature'}</button>
                  <button disabled={acting} onClick={() => runAction(p.id, 'suspend')} data-testid={ADMIN.providerSuspend} className="flex items-center gap-1.5 rounded-lg bg-destructive/15 px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/25"><Ban className="h-3.5 w-3.5" /> Suspend</button>
                </>
              )}
              {p.status === 'suspended' && (
                <button disabled={acting} onClick={() => runAction(p.id, 'approve')} className="flex items-center gap-1.5 rounded-lg bg-success/15 px-3 py-1.5 text-xs font-medium text-success hover:bg-success/25"><Check className="h-3.5 w-3.5" /> Reinstate</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
